import type { FunctionComponent } from 'react'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'

import { useKindeAuth } from '@kinde-oss/kinde-auth-react'

import { getQueryString, isLocal, Loader } from '../../../shared'
import { paywallUrls } from '../../../shared/paywall-urls'
import { ErrorModal } from '../components/ErrorModal'
import { accountPagesDataLayer, getIsValidReferrer, setTransactionName } from '../utils'

const LoginPage: FunctionComponent = () => {
  const kinde = useKindeAuth()

  const [paywallState] = useState(paywallUrls())

  const [showManualRedirect, setShowManualRedirect] = useState(false)

  setTransactionName('account/login')

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowManualRedirect(true)
    }, 2500)

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const fetchKindeRedirectUrl = () => {
      let kindeRedirectUrl = window.origin
      const maybeReturnUrl = getQueryString('return_url')
      const returnUrlParamPresent = typeof maybeReturnUrl === 'string'

      const maybeReferrerPath = getQueryString('referrer_path')
      const referrerPathParamPresent = typeof maybeReferrerPath === 'string'
      const isValidReferrer = getIsValidReferrer(document?.referrer)

      const redirectUrl = new URLSearchParams(window.location.search).get('redirect')
      if (redirectUrl) {
        if (getIsValidReferrer(redirectUrl)) {
          kindeRedirectUrl = redirectUrl
        }
      } else if (returnUrlParamPresent) {
        kindeRedirectUrl = `${location.origin}/${maybeReturnUrl}`
      } else if (isValidReferrer && referrerPathParamPresent) {
        const referrerUrl = new URL(document.referrer)
        kindeRedirectUrl = `${referrerUrl.origin}/${maybeReferrerPath}`
      } else if (isValidReferrer) {
        kindeRedirectUrl = document.referrer
      }

      return kindeRedirectUrl
    }
    const kindeLogin = () => {
      const domain = isLocal() ? 'localhost' : '.which.co.uk'

      if (!kinde.isAuthenticated) {
        document.cookie = `sessionId=temp;path=/;domain=${domain};expires=Fri, 31 Dec 9999 23:59:59 GMT`
        document.cookie = `blaize_session=temp;path=/;domain=${domain};expires=Fri, 31 Dec 9999 23:59:59 GMT`
      }

      sessionStorage.setItem('kindeRedirect', fetchKindeRedirectUrl())
      const redirectUri =
        typeof window !== 'undefined' && window.location?.origin
          ? `${window.location.origin}/login-redirect`
          : String(paywallState.redirectFeatureUri || '')
      kinde.login({
        redirectURL: redirectUri,
      })
    }
    if (kinde && !kinde.isLoading) {
      kindeLogin()
    }
  }, [paywallState, kinde, kinde.isLoading])

  return (
    <>
      <Helmet>
        <meta httpEquiv="Cache-Control" content="no-store, no-cache, must-revalidate" />
        <meta httpEquiv="Pragma" content="no-cache" />
        <meta httpEquiv="Expires" content="0" />
        <title key="title">Login to which.co.uk | Discover Best Buys & Don't Buys - Which?</title>
        <link key="canonical" rel="canonical" href="https://www.which.co.uk/login" />
        <meta
          key="twitter:title"
          name="twitter:title"
          content="Login to which.co.uk | Discover Best Buys & Don't Buys - Which?"
        />
        <meta
          key="og:title"
          property="og:title"
          content="Login to which.co.uk | Discover Best Buys & Don't Buys - Which?"
        />
        <meta key="twitter:image" name="twitter:image" content="https://www.which.co.uk/login" />
        <meta key="twitter:image:alt" name="twitter:image:alt" content="" />
        <meta key="og:image" property="og:image" content="https://www.which.co.uk/login" />
        <meta
          key="description"
          name="description"
          content="From this page members can log directly into which.co.uk. Access thousands of expertly tested independent reviews to help you make confident choices. Please have your email address or username and password ready."
        />
        <meta
          key="og:description"
          property="og:description"
          content="From this page members can log directly into which.co.uk. Access thousands of expertly tested independent reviews to help you make confident choices. Please have your email address or username and password ready."
        />
        <meta
          key="twitter:description"
          name="twitter:description"
          content="From this page members can log directly into which.co.uk. Access thousands of expertly tested independent reviews to help you make confident choices. Please have your email address or username and password ready."
        />
        <meta key="fb:pages" property="fb:pages" content="" />
        <meta key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta key="twitter:site" name="twitter:site" content="@whichuk" />
        <meta key="twitter:creator" name="twitter:creator" content="@whichuk" />
        <meta key="twitter:url" name="twitter:url" content="https://www.which.co.uk/login" />
        <meta key="og:type" property="og:type" content="website" />
        <meta key="og:url" property="og:url" content="https://www.which.co.uk/login" />
        <meta key="og:site_name" property="og:site_name" content="Which?" />
        <meta key="og:locale" property="og:locale" content="en_GB" />
        <meta key="language" name="language" content="en" />
        <meta key="country" name="country" content="GB" />
        <link key="favicon" rel="shortcut icon" href="/favicon.ico" type="image/ico" />
        <meta key="fb:app_id" property="fb:app_id" content="320949621749168" />
        <meta key="robots" name="robots" content="index, follow" />
        <script>
          {accountPagesDataLayer({
            content_type: 'login',
          })}
        </script>
      </Helmet>
      <Loader />
      <ErrorModal
        httpErrorCode={'408'}
        showModal={showManualRedirect}
        setShowModal={setShowManualRedirect}
        loginPage
      />
    </>
  )
}

export default LoginPage
